import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
	on,
	off,
	getWindowHeight,
	getBoundingClientRect,
} from '@sparefoot/react-core/utils/dom';
import { withShowHide } from '@sparefoot/react-core/decorators';
import { compose } from 'redux';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Calendar } from 'components/core/Calendar';
import { Input } from 'components/core/Input';

import './Datepicker.scss';

export const enhance = compose(withShowHide('calendar'));

export function Datepicker({
	allValid,
	calendarShowing,
	closures,
	error,
	helpText,
	id,
	label,
	name,
	onShowCalendar,
	onHideCalendar,
	placeholder,
	reservationWindow,
	today = new Date(),
	withIcon,
	value,
	onChange,
	onDateClick: onDateClickProp,
}) {
	const [instance, setInstance] = useState(null);

	const position = useMemo(() => {
		if (!instance) {
			return undefined;
		}
		const rect = getBoundingClientRect(instance);
		const above = rect.bottom + 250 > getWindowHeight();
		return above ? 'above' : 'below';
	}, [calendarShowing, instance]); // eslint-disable-line react-hooks/exhaustive-deps

	const getRef = useCallback((node) => {
		setInstance(node);
	}, []);

	const handleDateClick = useCallback(
		(date) => {
			onHideCalendar();
			onChange({
				target: {
					name,
					value: date,
				},
			});

			if (onDateClickProp) {
				onDateClickProp(date);
			}
		},
		[onHideCalendar, onChange, name, onDateClickProp],
	);

	const handlePageClick = useCallback(
		(event) => {
			if (!instance?.contains(event.target)) {
				onHideCalendar();
			}
		},
		[instance, onHideCalendar],
	);

	useEffect(() => {
		on('click', handlePageClick);
		return () => {
			off('click', handlePageClick);
		};
	}, [handlePageClick]);

	return (
		<div
			ref={getRef}
			className={classnames('ss-datepicker', position, {
				'with-icon': withIcon,
			})}
		>
			<Input
				name={name}
				id={id}
				helpText={helpText}
				label={label}
				value={value}
				onFocus={onShowCalendar}
				placeholder={placeholder}
				error={error}
				readOnly
			/>
			<CSSTransition
				classNames="transition-fade-in"
				in={calendarShowing}
				unmountOnExit
				timeout={150}
			>
				<Calendar
					closures={closures}
					reservationWindow={reservationWindow}
					today={today}
					selectedDate={value}
					onDateClick={handleDateClick}
					allValid={allValid}
				/>
			</CSSTransition>
		</div>
	);
}

Datepicker.propTypes = {
	allValid: PropTypes.bool,
	calendarShowing: PropTypes.bool,
	closures: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
	error: PropTypes.string,
	helpText: PropTypes.string,
	id: PropTypes.string,
	label: PropTypes.string,
	name: PropTypes.string.isRequired,
	onShowCalendar: PropTypes.func,
	onHideCalendar: PropTypes.func,
	placeholder: PropTypes.string,
	reservationWindow: PropTypes.instanceOf(Date),
	today: PropTypes.instanceOf(Date),
	value: PropTypes.string.isRequired,
	withIcon: PropTypes.bool,
	onChange: PropTypes.func,
	onDateClick: PropTypes.func,
};

export default enhance(Datepicker);
